import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';

interface Item {
    id: number;
    category: string;
    sub_category: string;
    title: string;
    author: string;
    status: string;
}

interface currentDashboardTableProps {
    items: Item[];
    onView: (id: number) => void;
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
}

export default function CurrentDashboardTable({ 
    items,
    onView,
    onEdit,
    onDelete 
}: currentDashboardTableProps) {
    const [latestPage, setLatestPage] = useState(1);
    const latestPerPage = 10;
    const [author, setAuthor] = useState<string | null>(null);
    const activeItems = items.filter(
        (item) => item.status === 'Approved' && item.author === author
    );
    const indexOfLastLatest = latestPage * latestPerPage;
    const indexOfFirstLatest = indexOfLastLatest - latestPerPage;
    // const currentLatest = items.slice(indexOfFirstLatest, indexOfLastLatest);
    const currentLatest = activeItems.slice(
        indexOfFirstLatest, 
        indexOfLastLatest
    );

    const totalPages = Math.ceil(activeItems.length / latestPerPage);

    const nextLatestPage = () => {
        if (latestPage < totalPages) { 
            setLatestPage(latestPage + 1); 
        }
    };

    const prevLatestPage = () => {
        if (latestPage > 1) { 
            setLatestPage(latestPage - 1);
        }
    };

    useEffect(() => {
        const storedAuthor = localStorage.getItem('author');
        setAuthor(storedAuthor);
    }, []);

    return (
        <>
            <table className="w-full">
                <thead className="bg-gray-200">
                    <tr>
                        <th style={{ width: '10%' }}>Category</th>
                        <th style={{ width: '15%' }}>Sub Category</th>
                        <th style={{ width: '25%' }}>Title</th>
                        <th style={{ width: '20%' }}>Author</th>
                        <th style={{ width: '10%' }}>Status</th>
                        <th style={{ width: '10%' }} className="text-center">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {currentLatest.length > 0 ? (
                        currentLatest.map(item => (
                            item.author === author && (
                                <tr key={item.id}>
                                    <td>{item.category}</td>
                                    <td>{item.sub_category}</td>
                                    <td>{item.title}</td>
                                    <td>{item.author}</td>
                                    <td>{item.status}</td>
                                    <td className="text-center">
                                        <div className="flex justify-center gap-1">
                                            <button 
                                                onClick={() => onView(item.id)} 
                                                data-tooltip-id='preview'>
                                                <FontAwesomeIcon icon={faEye} className="text-white bg-red-600 rounded-sm p-1" />
                                            </button>

                                            <button 
                                                onClick={() => onEdit(item.id)} 
                                                data-tooltip-id='edit'>
                                                <FontAwesomeIcon icon={faPenToSquare} className="text-white bg-red-600 rounded-sm p-1" />
                                            </button>

                                            <button 
                                                onClick={() => onDelete(item.id)} 
                                                data-tooltip-id='delete'>
                                                <FontAwesomeIcon icon={faTrash} className="text-white bg-gray-600 rounded-sm p-1" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )
                        ))
                    ) : (
                        <tr>
                            <td colSpan={6} className="text-center py-4">
                                No data available!
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
                
            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex justify-center gap-2 items-center mt-2 space-x-2">
                    <button
                        onClick={() => setLatestPage(1)}
                        className="px-3 py-1 rounded disabled:opacity-50 Univers-BoldCondensed text-white bg-gray-500"
                        disabled={latestPage === 1}>
                        FIRST
                    </button>

                    <button
                        onClick={prevLatestPage}
                        className="px-3 py-1 rounded disabled:opacity-50 Univers-BoldCondensed text-white bg-gray-500"
                        disabled={latestPage === 1}>
                        PREVIOUS
                    </button>

                    {[...Array(totalPages)].map((_, index) => {
                        const pageNum = index + 1;

                        // Display only the current page, one before, and one after it (3 pages at most)
                        const pageRange = 1; // Number of pages before and after the current page
                        const startPage = Math.max(1, latestPage - pageRange); // Prevent going below 1
                        const endPage = Math.min(totalPages, latestPage + pageRange); // Prevent going above totalPages

                        // Only display 3 pages: current page, one before, one after
                        if (pageNum >= startPage && pageNum <= endPage) {
                            return (
                            <button
                            key={pageNum}
                            onClick={() => setLatestPage(pageNum)}
                            className={`px-3 py-1 Univers-BoldCondensed text-xs rounded ${
                                latestPage === pageNum
                                ? "bg-red-600 text-white"
                                : "bg-gray-200 text-black"
                            }`}>
                            {pageNum}
                            </button>
                            );
                        }

                        return null; 
                    })}

                    <button
                        onClick={nextLatestPage}
                        className="px-3 py-1 rounded disabled:opacity-50 Univers-BoldCondensed text-white bg-red-500"
                        disabled={latestPage === Math.ceil(items.length / latestPerPage)}>
                        NEXT
                    </button>

                    <button
                        onClick={() => setLatestPage(totalPages)}
                        className="px-3 py-1 rounded disabled:opacity-50 Univers-BoldCondensed text-white bg-red-500"
                        disabled={latestPage === totalPages}>
                        LAST
                    </button>
                </div>
            )}
        </>
    )
}